import React, { useEffect, useState, useRef } from 'react';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend, Title, Filler, ScatterController } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import crosshairPlugin from 'chartjs-plugin-crosshair';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, Title, Filler, ScatterController, crosshairPlugin);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// X-axis bounds
const X_MIN = -50;
const X_MAX = 50;

// Bucket size for binned mean (in bps)
const BUCKET_SIZE = 5;

const VolatilityChart = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoverRVIV, setHoverRVIV] = useState(null);
    const chartRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${API_BASE_URL}/data/volatility`, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`Failed to fetch volatility data: ${response.statusText}`);
                }

                const result = await response.json();
                if (!isMounted) return;
                setData(result.data || []);
            } catch (err) {
                if (err.name === 'AbortError') {
                    return;
                }
                if (!isMounted) return;
                setError(err.message);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, []);

    if (loading) {
        return <p style={{ textAlign: 'center', padding: '30px' }}>Loading volatility data...</p>;
    }

    if (error) {
        return <p style={{ textAlign: 'center', padding: '30px', color: 'red' }}>Error: {error}</p>;
    }

    if (!data || data.length === 0) {
        return <p style={{ textAlign: 'center', padding: '30px' }}>No volatility data available.</p>;
    }

    // Filter to valid points with forward returns
    const validPoints = data.filter(row =>
        row.forward_return_1mo !== null &&
        row.forward_return_1mo !== undefined
    );

    if (validPoints.length === 0) {
        return <p style={{ textAlign: 'center', padding: '30px' }}>Volatility data loaded, but no forward returns are available yet.</p>;
    }

    // Create scatter points, clipped to X bounds
    // Use pre-calculated forward_return_normalized (normalized 1-mo forward return)
    const scatterPoints = validPoints
        .filter(row => row.forward_return_normalized !== null && row.forward_return_normalized !== undefined)
        .map(row => ({
            x: Number(row.rv_iv),
            y: Number(row.forward_return_normalized),
            date: row.date
        }))
        .filter(pt => pt.x >= X_MIN && pt.x <= X_MAX && !isNaN(pt.y));

    const pointColor = 'rgba(120, 120, 120, 0.4)';  // Gray scatter dots

    // Calculate rolling bucketed mean - overlapping windows
    // For each X position, compute mean of all points within ±HALF_WINDOW bps
    const bucketedMean = [];
    const HALF_WINDOW = BUCKET_SIZE / 2;  // ±2.5 bps for 5 bps window
    const STEP_SIZE = 0.5;  // Step every 0.5 bps for smooth line

    for (let xPos = X_MIN; xPos <= X_MAX; xPos += STEP_SIZE) {
        // Get all points within ±HALF_WINDOW of xPos
        const pointsInWindow = scatterPoints.filter(
            pt => pt.x >= xPos - HALF_WINDOW && pt.x < xPos + HALF_WINDOW
        );

        if (pointsInWindow.length >= 3) {
            const meanReturn = pointsInWindow.reduce((sum, pt) => sum + pt.y, 0) / pointsInWindow.length;

            bucketedMean.push({
                x: xPos,
                y: meanReturn,
                count: pointsInWindow.length
            });
        }
    }

    // Fixed Y bounds: -2 to +2 (normalized units)
    const yMin = -2;
    const yMax = 2;

    // Current RV-IV line - find the row with the latest date that has rv_iv data
    // Sort by date to ensure we get the most recent
    const sortedData = [...data].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;  // Descending order
    });

    // Find the most recent row with rv_iv value
    const latestWithRVIV = sortedData.find(row => row.rv_iv !== null && row.rv_iv !== undefined);
    const latestDataRow = latestWithRVIV || sortedData[0];

    const currentRVIV = latestDataRow && latestDataRow.rv_iv !== null && latestDataRow.rv_iv !== undefined
        ? Number(latestDataRow.rv_iv)
        : null;
    const latestDate = latestDataRow?.date || 'Latest';


    const verticalLineDataset = (currentRVIV !== null && currentRVIV >= X_MIN && currentRVIV <= X_MAX)
        ? {
            type: 'line',
            label: `Current RV-IV (${latestDate})`,
            data: [
                { x: currentRVIV, y: yMin },
                { x: currentRVIV, y: yMax }
            ],
            borderColor: 'rgba(220, 53, 69, 0.85)',
            borderWidth: 2,
            borderDash: [6, 6],
            pointRadius: 0
        }
        : null;

    // Horizontal zero line
    const zeroLineDataset = {
        type: 'line',
        label: 'Zero Line',
        data: [
            { x: X_MIN, y: 0 },
            { x: X_MAX, y: 0 }
        ],
        borderColor: 'rgba(0, 0, 0, 0.6)',
        borderWidth: 1.5,
        pointRadius: 0,
        order: 3
    };

    // Threshold for "good" returns (in normalized units)
    const RETURN_THRESHOLD = 0.2;

    // Create gradient for the bucketed mean line fill
    const createGradient = (ctx, chartArea) => {
        if (!chartArea) return 'rgba(65, 105, 225, 0.25)';

        const { top, bottom } = chartArea;
        const gradient = ctx.createLinearGradient(0, top, 0, bottom);

        // Map Y values to gradient positions
        // Sharp transition at threshold: blue above, red below
        const range = yMax - yMin;
        const thresholdPos = (yMax - RETURN_THRESHOLD) / range;

        gradient.addColorStop(0, 'rgba(65, 105, 225, 0.35)');  // Blue at top
        gradient.addColorStop(thresholdPos - 0.01, 'rgba(65, 105, 225, 0.3)');  // Blue just above threshold
        gradient.addColorStop(thresholdPos + 0.01, 'rgba(220, 53, 69, 0.25)');  // Red just below threshold
        gradient.addColorStop(1, 'rgba(220, 53, 69, 0.35)');  // Red at bottom

        return gradient;
    };

    // Create gradient for the line itself - sharp transition at threshold
    const createLineGradient = (ctx, chartArea) => {
        if (!chartArea) return 'rgba(65, 105, 225, 0.9)';

        const { top, bottom } = chartArea;
        const gradient = ctx.createLinearGradient(0, top, 0, bottom);

        const range = yMax - yMin;
        const thresholdPos = (yMax - RETURN_THRESHOLD) / range;

        gradient.addColorStop(0, 'rgba(65, 105, 225, 1)');  // Blue at top
        gradient.addColorStop(thresholdPos - 0.01, 'rgba(65, 105, 225, 1)');  // Blue just above threshold
        gradient.addColorStop(thresholdPos + 0.01, 'rgba(220, 53, 69, 1)');  // Red just below threshold
        gradient.addColorStop(1, 'rgba(220, 53, 69, 1)');  // Red at bottom

        return gradient;
    };

    const chartData = {
        datasets: [
            {
                type: 'scatter',
                label: 'Data Points',
                data: scatterPoints,
                backgroundColor: pointColor,
                borderColor: pointColor,
                pointRadius: 1.5,
                pointHoverRadius: 3,
                order: 3
            },
            {
                type: 'line',
                label: 'Mean',
                data: bucketedMean,
                borderColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    return createLineGradient(ctx, chartArea);
                },
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    return createGradient(ctx, chartArea);
                },
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0.3,
                stepped: false,
                fill: 'origin',
                order: 1
            },
            zeroLineDataset,
            ...(verticalLineDataset ? [verticalLineDataset] : [])
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    filter: (legendItem) => {
                        // Show Mean and Current RV-IV in legend
                        return legendItem.text === 'Mean' ||
                               legendItem.text.startsWith('Current RV-IV');
                    }
                }
            },
            tooltip: {
                enabled: false  // Using custom hover display instead
            },
            title: {
                display: false
            },
            crosshair: {
                line: {
                    color: 'rgba(0, 0, 0, 0.8)',
                    width: 1,
                    dashPattern: [5, 5]
                },
                sync: { enabled: false },
                zoom: { enabled: false },
                snap: { enabled: false }
            }
        },
        scales: {
            x: {
                type: 'linear',
                min: X_MIN,
                max: X_MAX,
                title: {
                    display: true,
                    text: 'RV-IV (bps)  ← Expensive | Cheap →'
                },
                grid: { color: 'rgba(0,0,0,0.08)' },
                ticks: {
                    stepSize: 10
                }
            },
            y: {
                title: {
                    display: true,
                    text: '1-mo Forward Return (normalized)'
                },
                min: yMin,
                max: yMax,
                grid: { color: 'rgba(0,0,0,0.08)' },
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    return (
        <div style={{ width: '100%' }}>

            {/* Main scatter chart */}
            <div style={{ height: '500px', position: 'relative' }} onMouseLeave={() => setHoverRVIV(null)}>
                {/* Hover RV-IV display at top */}
                <div style={{
                    position: 'absolute',
                    top: '35px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 10,
                    pointerEvents: 'none',
                    opacity: hoverRVIV !== null ? 1 : 0,
                    transition: 'opacity 0.1s'
                }}>
                    RV-IV: {hoverRVIV !== null ? hoverRVIV.toFixed(1) : '--'} bps
                </div>
                <Chart
                    ref={chartRef}
                    type="scatter"
                    data={chartData}
                    options={{
                        ...options,
                        onHover: (event, elements, chart) => {
                            if (!chart.chartArea) return;
                            const { left, right } = chart.chartArea;
                            const x = event.native?.offsetX;
                            if (x !== undefined && x >= left && x <= right) {
                                const xValue = chart.scales.x.getValueForPixel(x);
                                setHoverRVIV(xValue);
                            } else {
                                setHoverRVIV(null);
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default VolatilityChart;